import React, {useEffect, useRef, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {toAbsoluteUrl} from '../../../../_gori/helpers'
import {Button} from '../../../../_gori/partials/widgets'

type Props = {
  show: boolean
  handleClose: () => void
  onAddFunds: () => Promise<void>
  logId?: string | number
  saveBeforeRedirect?: boolean
}

const InsufficientBalanceModal: React.FC<Props> = ({
  show,
  handleClose,
  onAddFunds,
  logId,
  saveBeforeRedirect = true,
}) => {
  const intl = useIntl()
  const [saving, setSaving] = useState(false)
  const mountedRef = useRef(true)

  useEffect(() => {
    return () => {
      mountedRef.current = false
    }
  }, [])

  const handleAddFunds = async () => {
    try {
      if (saveBeforeRedirect) setSaving(true)
      await onAddFunds()
    } finally {
      if (saveBeforeRedirect && mountedRef.current) {
        setSaving(false)
      }
    }
  }

  return (
    <Modal
      id='gori_modal_insufficient_balance'
      tabIndex={-1}
      centered
      dialogClassName='mw-650px h-auto'
      show={show}
      onHide={saving ? undefined : handleClose}
      backdrop='static'
      keyboard={!saving}
    >
      {saving ? (
        <Modal.Body className='d-flex align-items-center justify-content-center h-100'>
          <div className='d-flex flex-column justify-content-center align-items-center pb-20'>
            <img
              src={toAbsoluteUrl('/media/gori/orders/reload.gif')}
              alt='Saving order'
              className='w-200px h-200px mb-n9'
            />
            <span className='fw-bolder fs-3'>
              {intl.formatMessage({id: 'SAVING_ORDER_PROGRESS'})}
            </span>
          </div>
        </Modal.Body>
      ) : (
        <>
          <Modal.Header closeButton>
            <Modal.Title bsPrefix='fw-semibold fs-2'>
              {intl.formatMessage({id: 'INSUFFICIENT_WALLET_BALANCE'})}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className='px-8 py-6'>
            <div className='d-flex flex-column align-items-center gap-3'>
              <img
                src={toAbsoluteUrl('/media/gori/errors/alert-triangle.svg')}
                alt='Warning'
                className='w-60px h-60px'
              />
              <div className='text-center insufficient-balance__body-text'>
                <p className='mb-0 fs-5 fw-bold'>
                  {intl.formatMessage({id: 'INSUFFICIENT_BALANCE_LINE_1'})}
                </p>
                <p className='mb-0 fs-5 fw-bold'>
                  {intl.formatMessage({id: 'INSUFFICIENT_BALANCE_LINE_2'})}
                </p>
              </div>
              {logId && (
                <div className='d-flex flex-column align-items-center w-100 p-3 text-center insufficient-balance__log-id-box'>
                  <span className='fs-6 fw-bold insufficient-balance__support-label'>
                    {intl.formatMessage({id: 'PROVIDE_ID_TO_SUPPORT'})}
                  </span>
                  <div className='fw-boldest fs-5 insufficient-balance__log-id-value'>
                    {intl.formatMessage({id: 'LOG_ID'}, {log_id: `[${logId}]`})}
                  </div>
                </div>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className='d-flex flex-column flex-sm-row justify-content-end gap-3 w-100'>
              <Button
                className='btn btn-advanced-secondary w-100 w-sm-auto'
                label={intl.formatMessage({id: 'CANCEL'})}
                loadingText={intl.formatMessage({id: 'CANCEL'})}
                event={handleClose}
                disabled={saving}
              />
              <Button
                className='btn btn-advanced-primary w-100 w-sm-auto'
                label={intl.formatMessage({id: 'ADD_FUNDS'})}
                loadingText={intl.formatMessage({id: 'ADD_FUNDS'})}
                event={handleAddFunds}
              />
            </div>
          </Modal.Footer>
        </>
      )}
    </Modal>
  )
}

export {InsufficientBalanceModal}
